import { createSlice } from '@reduxjs/toolkit';
import { InvoiceState } from '../types/invoiceTypes';
import {
  createInvoice,
  fetchNextInvoiceNumber,
  fetchInvoices,
  fetchInvoiceById,
  updateInvoice,
  searchInvoices,
} from '../actions/invoiceActions';

const initialState: InvoiceState = {
  invoices: [],
  searchInvoices: [],
  currentInvoice: null,
  pagination: null,
  searchPagination: null,
  loading: false,
  error: null,
  createSuccess: false,
  nextInvoiceNumber: null,
  isFirstTime: false,
  fetchingNextNumber: false,
  fetchingInvoices: false,
  searchingInvoices: false,
};

const invoiceSlice = createSlice({
  name: 'invoice',
  initialState,
  reducers: {
    // Reset create success flag
    resetCreateSuccess: state => {
      state.createSuccess = false;
    },
    // Clear error
    clearError: state => {
      state.error = null;
    },
    // Clear current invoice
    clearCurrentInvoice: state => {
      state.currentInvoice = null;
    },
  },
  extraReducers: builder => {
    // Fetch Next Invoice Number
    builder
      .addCase(fetchNextInvoiceNumber.pending, state => {
        state.fetchingNextNumber = true;
        state.error = null;
      })
      .addCase(fetchNextInvoiceNumber.fulfilled, (state, action) => {
        state.fetchingNextNumber = false;
        state.nextInvoiceNumber = action.payload.data;
        state.isFirstTime = action.payload.data.isFirstTime;
        state.error = null;
      })
      .addCase(fetchNextInvoiceNumber.rejected, (state, action) => {
        state.fetchingNextNumber = false;
        state.error = action.payload as string;
      });

    // Create Invoice
    builder
      .addCase(createInvoice.pending, state => {
        state.loading = true;
        state.error = null;
        state.createSuccess = false;
      })
      .addCase(createInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.currentInvoice = action.payload.data;
        state.invoices.unshift(action.payload.data); // Add to beginning of list
        state.createSuccess = true;
        state.error = null;
      })
      .addCase(createInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.createSuccess = false;
      });

    // Fetch Invoices
    builder
      .addCase(fetchInvoices.pending, state => {
        state.fetchingInvoices = true;
        state.error = null;
      })
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        state.fetchingInvoices = false;
        // API returns { success: true, data: { invoices: [], pagination: {} } }
        // So action.payload.data contains { invoices: [], pagination: {} }
        const invoicesData = Array.isArray(action.payload.data?.invoices)
          ? action.payload.data.invoices
          : [];
        state.invoices = invoicesData;
        state.pagination = action.payload.data?.pagination || null;
        state.error = null;
      })
      .addCase(fetchInvoices.rejected, (state, action) => {
        state.fetchingInvoices = false;
        state.error = action.payload as string;
        // Reset to empty array on error
        state.invoices = [];
        state.pagination = null;
      });

    // Fetch invoice by id
    builder
      .addCase(fetchInvoiceById.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvoiceById.fulfilled, (state, action) => {
        state.loading = false;
        // action.payload.data should contain invoice object
        state.currentInvoice = action.payload.data;
        state.error = null;
      })
      .addCase(fetchInvoiceById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update invoice
    builder
      .addCase(updateInvoice.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.currentInvoice = action.payload.data;
        // Update invoices list if present
        if (Array.isArray(state.invoices)) {
          const idx = state.invoices.findIndex(inv => inv.id === action.payload.data.id);
          if (idx !== -1) state.invoices[idx] = action.payload.data;
        }
        state.error = null;
      })
      .addCase(updateInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Search Invoices
    builder
      .addCase(searchInvoices.pending, state => {
        state.searchingInvoices = true;
        state.error = null;
      })
      .addCase(searchInvoices.fulfilled, (state, action) => {
        state.searchingInvoices = false;
        state.searchInvoices = action.payload.data.invoices || [];
        state.searchPagination = action.payload.data.pagination || null;
        state.error = null;
      })
      .addCase(searchInvoices.rejected, (state, action) => {
        state.searchingInvoices = false;
        state.error = action.payload as string;
      });
  },
});

export const { resetCreateSuccess, clearError, clearCurrentInvoice } = invoiceSlice.actions;
export default invoiceSlice.reducer;
